import { AccountBalanceWallet, Warning } from "@mui/icons-material";
import { Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { IProgramaFidelidade } from "../../../../../promocoes/interfaces/IProgramaFidelidade";
import { IClienteProgramaFidelidade } from "../../../../../clientes/interface/IClienteProgramaFidelidade";
import { ICupom } from "../../../../interface/ICupom";
import { ICategoria } from "../../../../../produtos/interfaces/ICategoria";
import { useResgateFidelidade } from "../../../../../promocoes/hooks/useResgateFidelidade";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { useMemo } from "react";

interface FidelidadeCashbackCardProps {
    programaAtivo: IProgramaFidelidade;
    programaCliente: IClienteProgramaFidelidade;
    cupons: ICupom[];
    aplicarCupom: (cupom: ICupom) => Promise<void>;
    adicionarProduto: (produto: any) => void;
    categorias: ICategoria[];
    produtos: any[];
}

export const FidelidadeCashbackCard = ({ cupons, aplicarCupom, programaCliente, programaAtivo }: FidelidadeCashbackCardProps) => {
    const { verificarJaResgatado, resgatandoCashback, resgatarCashback } = useResgateFidelidade({
        aplicarCupom,
        cupons,
    });

    const jaResgatado = verificarJaResgatado(programaCliente.programaFidelidadeUuid);
    const isResgatando = resgatandoCashback === programaCliente.programaFidelidadeUuid;
    const { real } = useMoeda();

    const estaVencido = useMemo(() => {
        if (!programaAtivo.diasVencimentoCashback || !programaCliente.ultimaAtualizacaoAs) return false;
        const dias = Math.floor(
            (Date.now() - new Date(programaCliente.ultimaAtualizacaoAs).getTime()) / (1000 * 60 * 60 * 24)
        );
        return dias > programaAtivo.diasVencimentoCashback;
    }, [programaAtivo.diasVencimentoCashback, programaCliente.ultimaAtualizacaoAs]);

    const dataVencimento = useMemo(() => {
        if (!programaAtivo.diasVencimentoCashback || !programaCliente.ultimaAtualizacaoAs) return null;
        const d = new Date(programaCliente.ultimaAtualizacaoAs);
        d.setDate(d.getDate() + programaAtivo.diasVencimentoCashback);
        return d;
    }, [programaAtivo.diasVencimentoCashback, programaCliente.ultimaAtualizacaoAs]);

    const diasParaVencer = useMemo(() => {
        if (!dataVencimento || estaVencido) return null;
        return Math.ceil((dataVencimento.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    }, [dataVencimento, estaVencido]);

    const atingiuLimite = useMemo(() => {
        if (!programaAtivo.limiteAcumuloCashback) return false;
        return (programaCliente.saldoReais ?? 0) >= programaAtivo.limiteAcumuloCashback;
    }, [programaAtivo.limiteAcumuloCashback, programaCliente.saldoReais]);

    return (
        <Paper
            key={programaCliente.uuid}
            elevation={4}
            sx={{
                p: "0.2rem 1rem",
                background: (theme) =>
                    theme.palette.mode === "dark"
                        ? "linear-gradient(135deg, rgba(25, 118, 210, 0.2) 0%, rgba(18, 18, 18, 1) 100%)"
                        : "linear-gradient(135deg, rgba(239, 246, 255, 1) 0%, rgba(255, 255, 255, 1) 100%)",
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ py: 1.5 }}>
                <Stack>
                    <Typography variant="caption" sx={{ fontSize: "10px", fontWeight: 700, color: "primary.main", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Saldo Cashback
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, fontSize: "24px", color: estaVencido ? "text.disabled" : "inherit" }}>
                        {real(programaCliente.saldoReais ?? 0)}
                    </Typography>
                    {atingiuLimite && !estaVencido && (
                        <Typography variant="caption" sx={{ fontSize: "10px", color: "warning.main" }}>
                            Limite máximo atingido
                        </Typography>
                    )}
                </Stack>
                <Stack alignItems="flex-end" gap={0.5}>
                    <AccountBalanceWallet sx={{ fontSize: "24px", color: "primary.main", opacity: 0.3 }} />
                    {estaVencido && (
                        <Chip label="VENCIDO" size="small" color="error" icon={<Warning />} sx={{ fontSize: "10px", height: "18px" }} />
                    )}
                    {!estaVencido && diasParaVencer !== null && diasParaVencer <= 7 && (
                        <Chip label={`Vence em ${diasParaVencer}d`} size="small" color="warning" sx={{ fontSize: "10px", height: "18px" }} />
                    )}
                </Stack>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 1 }}>
                <Stack>
                    <Typography variant="caption" sx={{ fontSize: "10px", color: "text.secondary" }}>
                        {programaCliente.saldoPedidos ?? 0} pedidos finalizados
                    </Typography>
                    {!estaVencido && dataVencimento && diasParaVencer !== null && diasParaVencer > 7 && (
                        <Typography variant="caption" sx={{ fontSize: "10px", color: "text.disabled" }}>
                            Válido até {dataVencimento.toLocaleDateString("pt-BR")}
                        </Typography>
                    )}
                    {estaVencido && (
                        <Typography variant="caption" sx={{ fontSize: "10px", color: "error.main" }}>
                            Saldo expirado. Realize um pedido para reativar.
                        </Typography>
                    )}
                </Stack>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => resgatarCashback(programaCliente)}
                    disabled={jaResgatado || isResgatando || !programaCliente.saldoReais || programaCliente.saldoReais <= 0 || estaVencido}
                    sx={{ fontSize: "12px", fontWeight: 700, py: 0.5, px: 1.5, textTransform: "none", minWidth: "auto" }}
                >
                    {jaResgatado ? "RESGATADO" : isResgatando ? "RESGATANDO..." : "RESGATAR"}
                </Button>
            </Stack>
        </Paper>
    );
}
